import {
  ListItemButton,
  ListItemIcon,
  ListSubheader,
  Stack,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import type { NavConfigProps, NavItemProps } from '../types';

type StyledItemProps = Omit<NavItemProps, 'item'> & {
  config: NavConfigProps;
};

export const StyledItem = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'active',
})<StyledItemProps>(({ active, depth, config, hasChild, disabled, theme }) => {
  const subItem = depth !== 1;

  const deepSubItem = depth > 2;

  const activeStyles = {
    root: {
      color:
        theme.palette.mode === 'light'
          ? theme.palette.primary[500]
          : theme.palette.primary[500],
      backgroundColor: disabled ? 'transparent' : theme.palette.primary[50],
      borderRadius: theme.spacing(1),

      '&:hover': {
        backgroundColor: theme.palette.primary[50],
        color:
          theme.palette.mode === 'light'
            ? theme.palette.primary[500]
            : theme.palette.primary[500],
      },
    },
    sub: {
      color: theme.palette.common.white,
      backgroundColor: theme.palette.primary[500],

      '&:hover': {
        backgroundColor: theme.palette.primary[100],
        color: theme.palette.common.white,
      },
    },
  };

  return {
    // Root item
    padding: config.itemPadding,
    margin: config.itemMargin,
    justifyContent: 'space-between',
    // marginBottom: config.itemGap,
    // borderRadius: config.itemRadius,
    minHeight: config.itemRootHeight,
    color: theme.palette.text.primary,

    '&:hover': {
      backgroundColor: theme.palette.primary[50],
      borderRadius: theme.spacing(1),
    },

    // Active root item
    ...((active || hasChild) && {
      ...activeStyles.root,
    }),

    // Sub item
    ...(subItem && {
      minHeight: config.itemSubHeight,
      // Active sub item
      ...(active && {
        ...activeStyles.sub,
      }),
    }),

    // Deep sub item
    ...(deepSubItem && {
      paddingLeft: theme.spacing(depth),
    }),

    // Disabled
    ...(disabled && {
      opacity: '1!important',
      color: theme.palette.grey[400],
    }),
  };
});

type StyledIconProps = {
  size?: number;
  active?: boolean;
  disabled?: boolean;
};

export const StyledIcon = styled(ListItemIcon, {
  shouldForwardProp: (prop) => prop !== 'active',
})<StyledIconProps>(({ size, active, disabled, theme }) => ({
  width: size,
  height: size,
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: theme.spacing(0.5),

  '& > svg': {
    color: `${
      active
        ? theme.palette.primary[400]
        : disabled
        ? theme.palette.grey[400]
        : theme.palette.grey[600]
    }`,
  },
}));

type StyledDotIconProps = {
  active?: boolean;
};

export const StyledDotIcon = styled('span')<StyledDotIconProps>(
  ({ active, theme }) => ({
    width: 4,
    height: 4,
    borderRadius: '50%',
    backgroundColor: theme.palette.text.disabled,
    transition: theme.transitions.create(['transform'], {
      duration: theme.transitions.duration.shorter,
    }),
    ...(active && {
      transform: 'scale(2)',
      backgroundColor: theme.palette.primary[500],
    }),
  })
);

type StyledSubheaderProps = {
  config: NavConfigProps;
};

export const StyledSubheader = styled(ListSubheader)<StyledSubheaderProps>(
  ({ config, theme }) => ({
    ...theme.typography.overline,
    fontSize: theme.typography.body3.fontSize,
    fontWeight: theme.typography.fontWeightSemiBold,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(0, 1),
    color: theme.palette.text.primary,
    transition: theme.transitions.create(['color'], {
      duration: theme.transitions.duration.shortest,
    }),
    textTransform: 'capitalize',
    width: '100%',
    height: config.itemSubHeight,
  })
);

export const StyledGroupsWrapper = styled(Stack)`
  gap: ${(p) => p.theme.spacing(0.5)};
`;
